import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, industriesAPI, speakersAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  FileDown, FileText, Loader2, RefreshCw, Search, DollarSign,
  ShoppingCart, Users, TrendingUp, ChevronLeft, ChevronRight, FilterX,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

const COLORS = ['#1B4F72', '#D4A03A', '#0e7490', '#047857', '#7c3aed', '#dc2626', '#2563eb', '#db2777', '#65a30d', '#ea580c'];

const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const fmtDate = (d) => {
  if (!d) return 'N/A';
  try {
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
  } catch {
    return d;
  }
};

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Translate the period picker into a date_from / date_to pair
const computeRange = (period, month, year, customFrom, customTo) => {
  const today = new Date();
  if (period === '7d' || period === '15d' || period === '30d') {
    const back = period === '7d' ? 6 : period === '15d' ? 14 : 29;
    const from = new Date(today);
    from.setDate(today.getDate() - back);
    return { date_from: toYMD(from), date_to: toYMD(today) };
  }
  if (period === 'month' && month) {
    const [y, m] = month.split('-').map(Number);
    const from = new Date(y, m - 1, 1);
    const to = new Date(y, m, 0); // last day of month
    return { date_from: toYMD(from), date_to: toYMD(to) };
  }
  if (period === 'year' && year) {
    return { date_from: `${year}-01-01`, date_to: `${year}-12-31` };
  }
  if (period === 'custom') {
    const r = {};
    if (customFrom) r.date_from = customFrom;
    if (customTo) r.date_to = customTo;
    return r;
  }
  return {}; // 'all'
};

const STATUS_LABEL = { completed: 'Paid', pending: 'Pending', payment_pending: 'Payment Pending' };
const statusBadge = (s) =>
  s === 'completed'
    ? 'bg-green-100 text-green-700'
    : s === 'pending'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-orange-100 text-orange-700';

const CurrencyTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2">
      <p className="text-xs text-slate-500">{label || p.payload.name}</p>
      <p className="text-sm font-bold text-navy-700">{fmtCurrency(p.value)}</p>
      {p.payload.title && <p className="text-xs text-slate-400 max-w-52 truncate">{p.payload.title}</p>}
    </div>
  );
};

export default function AdminReports() {
  const [rows, setRows] = useState([]);
  const [charts, setCharts] = useState({ revenue_over_time: [], revenue_by_speaker: [], revenue_by_industry: [], revenue_by_webinar_code: [] });
  const [stats, setStats] = useState({ total_revenue: 0, total_items: 0, total_orders: 0, total_customers: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [industries, setIndustries] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [exportingCsv, setExportingCsv] = useState(false);

  const [filters, setFilters] = useState({
    webinar_code: '', webinar_title: '', speaker_name: '', industry_id: 'all', status: 'completed',
    period: 'all', month: '', year: '', date_from: '', date_to: '',
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2016 }, (_, i) => String(currentYear - i));

  const buildParams = useCallback((overrides = {}) => {
    const f = { ...filters, ...overrides };
    const p = {};
    if (f.webinar_code) p.webinar_code = f.webinar_code;
    if (f.webinar_title) p.webinar_title = f.webinar_title;
    if (f.speaker_name) p.speaker_name = f.speaker_name;
    if (f.industry_id && f.industry_id !== 'all') p.industry_id = f.industry_id;
    if (f.status) p.status = f.status;
    const range = computeRange(f.period, f.month, f.year, f.date_from, f.date_to);
    if (range.date_from) p.date_from = range.date_from;
    if (range.date_to) p.date_to = range.date_to;
    return p;
  }, [filters]);

  const fetchReport = useCallback(async (pageNum = page, overrides = {}) => {
    setLoading(true);
    try {
      const res = await adminAPI.getSalesReport({ page: pageNum, per_page: 50, ...buildParams(overrides) });
      setRows(res.data.rows || []);
      setCharts(res.data.charts || {});
      setStats(res.data.stats || {});
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load sales report');
    } finally {
      setLoading(false);
    }
  }, [page, buildParams]);

  useEffect(() => { fetchReport(page); }, [page]); // eslint-disable-line

  useEffect(() => {
    (async () => {
      try {
        const res = await industriesAPI.getFlat();
        setIndustries(res.data || []);
      } catch { /* ignore */ }
      try {
        const res = await speakersAPI.getDropdown();
        setSpeakers(res.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const applyFilters = () => { setPage(1); fetchReport(1); };
  const resetFilters = () => {
    const blank = { webinar_code: '', webinar_title: '', speaker_name: '', industry_id: 'all', status: 'completed', period: 'all', month: '', year: '', date_from: '', date_to: '' };
    setFilters(blank); setPage(1); fetchReport(1, blank);
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const res = await adminAPI.exportSalesReportCsv(buildParams());
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_report_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      console.error(e);
      toast.error('Failed to export CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  const statCards = [
    { label: 'Total Revenue', value: fmtCurrency(stats.total_revenue), icon: DollarSign, cls: 'bg-purple-100 text-purple-600' },
    { label: 'Line Items Sold', value: (stats.total_items || 0).toLocaleString(), icon: TrendingUp, cls: 'bg-blue-100 text-blue-600' },
    { label: 'Orders', value: (stats.total_orders || 0).toLocaleString(), icon: ShoppingCart, cls: 'bg-emerald-100 text-emerald-600' },
    { label: 'Customers', value: (stats.total_customers || 0).toLocaleString(), icon: Users, cls: 'bg-amber-100 text-amber-600' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Sales Report</h1>
          <p className="text-slate-500 mt-1">Completed (paid) webinar sales with net revenue, charts and exports</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportCsv} disabled={exportingCsv} className="bg-slate-800 hover:bg-slate-900" data-testid="export-csv-btn">
            {exportingCsv ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${s.cls.split(' ')[0]}`}>
                  <s.icon className={`w-5 h-5 ${s.cls.split(' ')[1]}`} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{s.label}</p>
                  <p className="text-xl font-bold text-slate-800">{s.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input placeholder="Webinar code" value={filters.webinar_code} onChange={(e) => setF('webinar_code', e.target.value)} data-testid="filter-webinar-code" />
            <Input placeholder="Webinar title" value={filters.webinar_title} onChange={(e) => setF('webinar_title', e.target.value)} data-testid="filter-webinar-title" />
            <Select value={filters.speaker_name || 'all'} onValueChange={(v) => setF('speaker_name', v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="filter-speaker"><SelectValue placeholder="Speaker" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Speakers</SelectItem>
                {speakers.map((s) => (
                  <SelectItem key={s.id} value={s.full_name}>{s.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.industry_id} onValueChange={(v) => setF('industry_id', v)}>
              <SelectTrigger data-testid="filter-industry"><SelectValue placeholder="Industry" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {industries.map((i) => (
                  <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Select value={filters.period} onValueChange={(v) => setF('period', v)}>
              <SelectTrigger className="w-44" data-testid="filter-period"><SelectValue placeholder="Period" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="7d">Last 7 Days</SelectItem>
                <SelectItem value="15d">Last 15 Days</SelectItem>
                <SelectItem value="30d">Last 30 Days</SelectItem>
                <SelectItem value="month">Specific Month</SelectItem>
                <SelectItem value="year">Specific Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {filters.period === 'month' && (
              <Input type="month" value={filters.month} onChange={(e) => setF('month', e.target.value)} className="w-44" data-testid="filter-month" />
            )}
            {filters.period === 'year' && (
              <Select value={filters.year} onValueChange={(v) => setF('year', v)}>
                <SelectTrigger className="w-32" data-testid="filter-year"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {filters.period === 'custom' && (
              <>
                <Input type="date" value={filters.date_from} onChange={(e) => setF('date_from', e.target.value)} className="w-40" data-testid="filter-date-from" />
                <span className="text-slate-400 text-sm">to</span>
                <Input type="date" value={filters.date_to} onChange={(e) => setF('date_to', e.target.value)} className="w-40" data-testid="filter-date-to" />
              </>
            )}

            <Select value={filters.status} onValueChange={(v) => setF('status', v)}>
              <SelectTrigger className="w-48" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="completed">Completed (Paid)</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="payment_pending">Payment Pending</SelectItem>
                <SelectItem value="all">All Statuses</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={applyFilters} className="bg-navy-600 hover:bg-navy-700" data-testid="apply-filters-btn">
              <Search className="w-4 h-4 mr-2" /> Apply
            </Button>
            <Button variant="outline" onClick={resetFilters} data-testid="reset-filters-btn">
              <FilterX className="w-4 h-4 mr-2" /> Reset
            </Button>
            <Button variant="outline" onClick={() => fetchReport(page)} data-testid="refresh-report-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-base">Revenue Over Time</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72" data-testid="chart-revenue-over-time">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.revenue_over_time || []} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1B4F72" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#1B4F72" stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
                  <Tooltip content={<CurrencyTooltip />} cursor={{ stroke: '#cbd5e1' }} />
                  <Area type="monotone" dataKey="revenue" stroke="#1B4F72" strokeWidth={2.5} fill="url(#revGrad)" dot={false} activeDot={{ r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Revenue by Speaker (Top 10)</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72" data-testid="chart-revenue-by-speaker">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.revenue_by_speaker || []} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <defs>
                    <linearGradient id="spkGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#0e7490" />
                      <stop offset="100%" stopColor="#22d3ee" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CurrencyTooltip />} cursor={{ fill: '#f1f5f9' }} />
                  <Bar dataKey="revenue" fill="url(#spkGrad)" radius={[0, 6, 6, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Revenue by Industry</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72" data-testid="chart-revenue-by-industry">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={charts.revenue_by_industry || []} dataKey="revenue" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {(charts.revenue_by_industry || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<CurrencyTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-base">Revenue by Webinar Code (Top 10)</CardTitle></CardHeader>
          <CardContent>
            <div className="h-80" data-testid="chart-revenue-by-webinar-code">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.revenue_by_webinar_code || []} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <defs>
                    <linearGradient id="codeGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#D4A03A" />
                      <stop offset="100%" stopColor="#f59e0b" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
                  <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CurrencyTooltip />} cursor={{ fill: '#f1f5f9' }} />
                  <Bar dataKey="revenue" fill="url(#codeGrad)" radius={[0, 6, 6, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Sales Detail</span>
            <span className="text-sm font-normal text-slate-500">Showing {rows.length} of {total.toLocaleString()} records</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-600" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No sales records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Webinar Title</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Variant</TableHead>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Purchased Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead>Speaker</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={`${r.order_id}-${i}`} className="hover:bg-slate-50">
                      <TableCell className="max-w-64"><p className="font-medium text-slate-800 truncate" title={r.webinar_title}>{r.webinar_title}</p></TableCell>
                      <TableCell><span className="font-mono text-xs text-slate-600">{r.webinar_code}</span></TableCell>
                      <TableCell><span className="text-xs text-slate-600">{r.variant}</span></TableCell>
                      <TableCell><span className="font-mono text-xs text-slate-500">{r.order_id}</span></TableCell>
                      <TableCell><span className="text-xs text-slate-600 whitespace-nowrap">{fmtDate(r.purchased_date)}</span></TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{r.customer_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{r.customer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell><span className="text-xs text-slate-600">{r.customer_contact || '—'}</span></TableCell>
                      <TableCell className="text-right"><span className="font-semibold text-slate-800">{fmtCurrency(r.revenue)}</span></TableCell>
                      <TableCell><span className="text-sm text-slate-700">{r.speaker_name || '—'}</span></TableCell>
                      <TableCell>
                        <span className="text-xs text-slate-600">
                          {r.industry}{r.sub_industry && r.sub_industry !== r.industry ? ` / ${r.sub_industry}` : ''}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded font-medium ${statusBadge(r.status)}`} data-testid="row-status">
                          {STATUS_LABEL[r.status] || r.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="prev-page-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="next-page-btn">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
